import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ExternalLinkIcon,
  FileTextIcon,
  RefreshCwIcon,
  SparklesIcon,
} from 'lucide-react';
import {
  Button,
  EmptyState,
  ErrorNotice,
  PageHeading,
  Panel,
  Pill,
  SkeletonTable,
  Tabs,
} from '@bcecloud/portal-ui';
import { errorMessage, getJson, sendJson } from '@/lib/api';

/**
 * One attachment: the file itself (inline for PDFs and images — the view
 * endpoint streams same-origin with the session cookie) and its extracted
 * document context on a second tab. Context is generated on demand — never
 * backfilled — through the same extraction the assistant's readAttachment
 * tool uses, so a click here also warms the assistant's cache.
 */

const CONTEXT_STATUS = {
  extracted: 'extracted',
  failed: 'failed',
  unsupported: 'unsupported',
} as const;

interface AttachmentContext {
  readonly docType?: string;
  readonly summary?: string;
  readonly keyPoints?: readonly string[];
  readonly people?: readonly string[];
  readonly organizations?: readonly string[];
  readonly dates?: readonly string[];
  readonly amounts?: readonly string[];
}

interface AttachmentDetail {
  readonly _id: string;
  readonly name: string;
  readonly type?: string | null;
  readonly description?: string | null;
  readonly context?: AttachmentContext | null;
  readonly contextStatus?: string | null;
  readonly contextError?: string | null;
  readonly createdAt: string;
}

type Tab = 'preview' | 'context';

function EntityList({ label, items }: { label: string; items?: readonly string[] }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <p className="mb-2 text-portal-micro font-bold uppercase tracking-wider text-portal-muted">
        {label}
      </p>
      <ul className="space-y-1.5">
        {items.map((item, index) => (
          <li key={index} className="text-portal-small leading-snug break-words text-portal-ink">
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function AttachmentPage(): React.ReactElement {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('preview');

  const attachment = useQuery({
    queryKey: ['attachment', id],
    queryFn: ({ signal }) => getJson<AttachmentDetail>(`/attachments/${id}`, undefined, signal),
    enabled: id.length === 24,
  });

  const generate = useMutation({
    mutationFn: (force: boolean) =>
      sendJson('post', `/attachments/${id}/context`, force ? { force: true } : {}),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['attachment', id] });
    },
  });

  if (attachment.isPending) return <SkeletonTable label="Loading attachment…" />;
  if (attachment.isError) {
    return <ErrorNotice title="Attachment unavailable" message={errorMessage(attachment.error)} />;
  }

  const data = attachment.data;
  const viewUrl = `/api/v3/attachments/${id}/view`;
  const type = data.type ?? '';
  const isImage = type.startsWith('image/');
  const isPdf = /pdf/i.test(type) || /\.pdf$/i.test(data.name);
  const context = data.context ?? null;

  return (
    <section className="animate-portal-rise">
      <div className="flex items-start justify-between gap-4">
        <PageHeading
          eyebrow="ATTACHMENT"
          title={data.name}
          {...(data.description ? { subtitle: data.description } : {})}
        />
        <Button variant="ghost" onClick={() => window.open(viewUrl, '_blank', 'noopener,noreferrer')}>
          <ExternalLinkIcon aria-hidden className="size-4" /> Open file
        </Button>
      </div>

      <Tabs<Tab>
        label="Attachment sections"
        items={[
          { key: 'preview', label: 'Preview' },
          { key: 'context', label: 'Context' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'preview' ? (
        isImage ? (
          <Panel className="grid place-items-center bg-black/95 p-2">
            <img src={viewUrl} alt={data.name} className="max-h-[75vh] w-auto object-contain" />
          </Panel>
        ) : isPdf ? (
          <Panel className="overflow-hidden p-0">
            <iframe src={viewUrl} title={data.name} className="h-[75vh] w-full border-0" />
          </Panel>
        ) : (
          <Panel>
            <EmptyState
              icon={<FileTextIcon aria-hidden className="size-6" />}
              title="No inline preview for this file type"
              message={`"${type || 'Unknown type'}" can't be shown here — use Open file to download or view it in a new tab.`}
            />
          </Panel>
        )
      ) : (
        <>
          {context && data.contextStatus === CONTEXT_STATUS.extracted ? (
            <Panel>
              <div className="flex items-center justify-between gap-3 border-b border-portal-line px-6 py-3.5">
                <span className="flex items-center gap-2.5">
                  {context.docType ? <Pill tone="success">{context.docType}</Pill> : null}
                  <span className="text-portal-micro text-portal-muted">
                    Extracted by the assistant — it can read this too, via readAttachment.
                  </span>
                </span>
                <button
                  type="button"
                  disabled={generate.isPending}
                  onClick={() => generate.mutate(true)}
                  className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-portal-button border border-portal-line px-2.5 py-1.5 text-portal-micro font-bold text-portal-muted hover:border-brand hover:text-brand disabled:opacity-50"
                >
                  <RefreshCwIcon
                    aria-hidden
                    className={`size-3.5 ${generate.isPending ? 'animate-spin' : ''}`}
                  />
                  Regenerate
                </button>
              </div>

              <div className="space-y-6 px-6 py-5">
                {context.summary ? (
                  <p className="max-w-3xl text-portal-body leading-relaxed text-portal-ink">
                    {context.summary}
                  </p>
                ) : null}

                {context.keyPoints && context.keyPoints.length > 0 ? (
                  <div className="rounded-portal-card border border-portal-line bg-portal-bg px-5 py-4">
                    <p className="mb-2.5 text-portal-micro font-bold uppercase tracking-wider text-portal-muted">
                      Key points
                    </p>
                    <ul className="grid grid-cols-1 gap-x-8 gap-y-1.5 lg:grid-cols-2">
                      {context.keyPoints.map((point, index) => (
                        <li key={index} className="flex gap-2 text-portal-small leading-snug text-portal-ink">
                          <span aria-hidden className="mt-[7px] size-1.5 shrink-0 rounded-full bg-brand" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                <div className="grid grid-cols-1 gap-x-8 gap-y-5 border-t border-portal-line pt-5 sm:grid-cols-2 lg:grid-cols-4">
                  <EntityList label="People" items={context.people} />
                  <EntityList label="Organizations" items={context.organizations} />
                  <EntityList label="Dates" items={context.dates} />
                  <EntityList label="Amounts" items={context.amounts} />
                </div>
              </div>
            </Panel>
          ) : data.contextStatus === CONTEXT_STATUS.unsupported ? (
            <ErrorNotice
              title="This file can't be read"
              message={data.contextError ?? 'Only PDFs and text files can be turned into context.'}
            />
          ) : (
            <Panel>
              <EmptyState
                icon={<SparklesIcon aria-hidden className="size-6" />}
                title={
                  data.contextStatus === CONTEXT_STATUS.failed
                    ? 'Extraction failed last time'
                    : 'No context yet'
                }
                message={
                  data.contextStatus === CONTEXT_STATUS.failed
                    ? (data.contextError ?? 'Something went wrong reading the file — try again.')
                    : 'Generate structured context from this document — a summary, key points, people, dates and amounts the assistant can use too.'
                }
              />
              <div className="flex justify-center pb-6">
                <Button onClick={() => generate.mutate(false)} disabled={generate.isPending}>
                  <SparklesIcon aria-hidden className="size-4" />
                  {generate.isPending ? 'Reading the document…' : 'Generate context'}
                </Button>
              </div>
              {generate.isError ? (
                <p className="pb-4 text-center text-portal-small text-red-600">
                  {errorMessage(generate.error)}
                </p>
              ) : null}
            </Panel>
          )}
        </>
      )}
    </section>
  );
}
